/**
 * Returns the value in a nested associative structure where keys
 * are the keys to go looking for values in obj. Returns undefined
 * when no values are present. If called without keys, the original
 * object is returned
 *
 * Example:
 *
 * const obj = {
 *  name: 'Joe',
 *  info: {
 *    hobbies: ['swimming']
 *  }
 * }
 *
 * getIn(obj, 'info', 'hobbies', 0) // 'swimming'
 *
 * @param {object} obj
 * @param  {...string|number} keys
 * @returns
 */

function getIn(obj, ...keys) {
    try {
        return keys.reduce((result, currentKey) => result[currentKey], obj);
    } catch (err) {
        return undefined;
    }
}

/**
 * No-op
 */

function noop() { }

/**
 * Returns the first element of a collection or array-like object, or undefined
 *
 * @param {*[]} coll
 * @return {*}
 */

function first(coll) {
    return coll[0];
}

/**
 * Returns the value passed to it without changing it
 *
 * @param {v} v
 * @returns {v} v
 */

function identity(v) {
    return v;
}

/**
 * Displays a notification
 *
 * @param {string} title
 * @param {string} message
 * @param {string} type
 * @param {number} duration
 */

function isEmpty(i) {
    const t = typeof i;
    switch (t) {
        case "string":
            return i.length === 0;
        case "number":
            return Number.isNaN(i);
        case "object":
            return Array.isArray(i) ? i.length === 0 : t === null || Object.keys(i).length === 0;
        case "undefined":
            return true;
        default:
            return false;
    }
}

function decodeHtml(html) {
    var txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
}

function partition(size, coll, paddingColl) {
    const result = [];
    for (let i = 0; i < coll.length; i += size) {
        const partitionedChunk = coll.slice(i, i + size);
        if (partitionedChunk.length < size && paddingColl) {
            while (partitionedChunk.length < size) {
                partitionedChunk.push(paddingColl.shift());
            }
        }
        result.push(partitionedChunk);
    }
    return result;
}

/**
 * Escapes all characters in a string that could conflict with JSON or the RegExp constructor.
 * 
 * @param {string} str - The string to be escaped.
 * @returns {string} - The escaped string.
 */
function escapeForRegExpAndJSON(str) {
    // Escape characters that are special in RegExp
    const escapedForRegExp = str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

    // Escape backslashes and double quotes for JSON
    const escapedForJSON = escapedForRegExp.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

    return escapedForJSON;
}

function unescapeFromRegExpAndJSON(str) {
    // Unescape backslashes and double quotes for JSON
    const unescapedFromJSON = str.replace(/\\"/g, '"').replace(/\\\\/g, '\\');

    // Unescape characters that were escaped for RegExp
    const unescapedFromRegExp = unescapedFromJSON.replace(/\\([-\/\\^$*+?.()|[\]{}])/g, '$1');

    return unescapedFromRegExp;
}

/**
 * 
 * @param {string} htmlString 
 * @returns string
 */
function escapeHtmlForJson(htmlString) {
    return htmlString.replace(/(?<!\\)"/g, '\\"');
}

/**
 * 
 * @param {string} escapedString 
 * @returns string
 */
function unescapeHtmlFromJson(escapedString) {
    return escapedString.replace(/\\"/g, '"');
}

/**
 * Zips together multiple arrays.
 * It takes N arrays as arguments and returns an array of N-element arrays,
 * where the i-th N-element array contains the i-th element from each of the input arrays.
 * The zipping stops as soon as the shortest input array is exhausted.
 *
 * @param {...Array<any>} arrays - The arrays to zip.
 * @returns {Array<Array<any>>} An array of arrays, where each inner array
 *                               contains elements from the input arrays at the same index.
 *                               Returns an empty array if no arguments are provided or
 *                               if any of the input arrays are empty.
 */
function zip(...arrays) {
  // If no arrays are provided, return an empty array.
  if (arrays.length === 0) {
    return [];
  }

  // Determine the length of the shortest array.
  // If any array is empty, minLength will be 0.
  const minLength = Math.min(...arrays.map(arr => {
    // Ensure each argument is an array; if not, treat its length as 0 for min calculation.
    // This makes it behave like zip with an empty iterable.
    if (!Array.isArray(arr)) {
        console.warn("Warning: zip received a non-array argument. It will be treated as an empty array for zipping.");
        return 0;
    }
    return arr.length;
  }));

  // If the shortest array has length 0 (i.e., an empty array was passed),
  // the result of zipping is an empty array.
  if (minLength === 0) {
    return [];
  }

  const result = [];
  for (let i = 0; i < minLength; i++) {
    const group = [];
    for (const arr of arrays) {
      // We only push if it's an actual array, otherwise we'd get errors
      // if a non-array was passed that somehow didn't result in minLength = 0
      if (Array.isArray(arr)) {
        group.push(arr[i]);
      }
    }
    result.push(group);
  }

  return result;
}


export {
    getIn,
    noop,
    first,
    identity,
    isEmpty,
    decodeHtml,
    partition,
    escapeForRegExpAndJSON,
    unescapeFromRegExpAndJSON,
    escapeHtmlForJson,
    unescapeHtmlFromJson,
    zip
};
